import React, { useEffect, useState } from 'react';
import { Text } from '@chakra-ui/layout';
import {
  Icon,
  useDisclosure,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Drawer,
  DrawerFooter,
  HStack,
  IconButton,
  Stack,
  Thead,
  Th,
  Tr,
  Tbody,
  Td,
  Checkbox,
  useToast,
  Spinner,
  FormLabel,
  FormControl,
  FormErrorMessage,
  InputGroup,
} from '@chakra-ui/react';
import { BsPlusCircle, FiEye } from 'react-icons/all';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosRequestConfig } from 'axios';
import { FiEdit } from 'react-icons/fi';
import { MultiSelect, Option } from 'react-multi-select-component';
import {
  Button,
  Datepicker,
  Heading,
  Input,
  Textarea,
  Table,
  Select,
  AutoComplete,
} from '~/components';
import { FileUploader } from '~/components/FileInput';
import { Equipment, SelectItem, SummarizedEquipments } from '~/types';
import { InsuranceView } from '~/types/insurance';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { parseDate } from '~/utils/parseDate';
import { getDayAfterXMonths } from '~/utils/getLastDayOfMonthAfterXMonths';
import { useAllInsurance } from '~/hooks/useInsurance/useInsurance';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';

interface InsuranceDialogProps {
  id_contrato: string;
  isEdit: boolean;
  isView: boolean;
  seguro?: InsuranceView;
  equipaments: SummarizedEquipments[];
}

export const InsuranceDialog: React.FC<InsuranceDialogProps> = ({
  id_contrato,
  isEdit = false,
  isView = false,
  seguro,
  equipaments,
}: InsuranceDialogProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const validationFormSchema = yup.object().shape({
    nu_apolice: yup
      .string()
      .required('Número da apólice obrigatório')
      .max(25, 'Número da apólice deve ter no máximo 25 caracteres'),
    dt_inicio: yup.date().required('Data de início obrigatória'),
    dt_encerramento: yup.date().required('Data de encerramento obrigatória'),
    arquivo: yup.string().required('Arquivo obrigatório'),
  });
  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
    clearErrors,
    setError,
    control,
  } = useForm({ resolver: yupResolver(validationFormSchema) });
  const { errors } = formState;

  const [dt_inicio, setDtInicio] = useState<Date | undefined>(undefined);
  const [dt_encerramento, setDtEncerramento] = useState<Date | undefined>(
    undefined,
  );
  const [nomeArquivo, setNomeArquivo] = useState<string | undefined>(undefined);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selecionarTodos, setSelecionarTodos] = useState(false);
  const [equipamentosSelecionados, setEquipamentosSelecionados] = useState<
    any[]
  >([]);
  const [listaEquipamentos, setListaEquipamentos] =
    useState<SummarizedEquipments[]>(equipaments);

  const [fetching, setFetching] = useState(false);

  const [selectedNumeroApolice, setSelectedNumeroApolice] = useState<
    Option[] | undefined
  >([]);

  const [formattedInsuranceList, setFormattedInsuranceList] = useState<
    Option[]
  >([]);
  const {
    data: insuranceList,
    isLoading: isLoadingInsuranceList,
    refetch: refetchInsurance,
  } = useAllInsurance({
    onSuccess: data => {
      setFormattedInsuranceList(
        data.map(insurance => ({
          value: insurance.nu_apolice.toString(),
          label: insurance.nu_apolice,
          disabled: false,
        })),
      );
    },
  });

  const handleOpen = () => {
    refetchInsurance();
    if (seguro) {
      setValue('nu_apolice', seguro.nu_apolice);
      setValue('dt_inicio', seguro.dt_inicio);
      setValue('dt_encerramento', seguro.dt_encerramento);
      setValue('observacao', seguro.observacoes);
      setValue('arquivo', seguro.arquivo);

      setDtInicio(parseDate(seguro.dt_inicio));
      setDtEncerramento(parseDate(seguro.dt_encerramento));
      setNomeArquivo(seguro.arquivo);
      setSelectedFile(null);
      setSelectedNumeroApolice([
        {
          value: seguro.nu_apolice,
          label: seguro.nu_apolice,
          disabled: true,
        },
      ]);

      setEquipamentosSelecionados(seguro.equipamentos);
      const equipamentos = equipaments?.map(eq => {
        const eqSelecionado = seguro.equipamentos?.find(
          (eqSeguro: any) => eqSeguro.id_equipamento === eq.id_equipamento,
        );
        if (eqSelecionado) {
          return { ...eq, selecionado: true };
        }
        return eq;
      });
      setListaEquipamentos(equipamentos || []);
      const todosSelecionados = equipamentos?.every(eq => eq.selecionado);
      setSelecionarTodos(todosSelecionados ?? false);
    } else {
      const lista = listaEquipamentos.map(eq => {
        return { ...eq, selecionado: true };
      });
      setListaEquipamentos(lista);
      const todosSelecionados = lista.every(eq => eq.selecionado);
      setSelecionarTodos(todosSelecionados);
      const equipamentos = lista.filter(eq => eq.selecionado);
      setEquipamentosSelecionados(equipamentos);
    }
    onOpen();
  };

  const handleClose = () => {
    clearErrors();
    clearValues();
    onClose();
  };

  const clearValues = () => {
    reset();
    setDtInicio(undefined);
    setDtEncerramento(undefined);
    setNomeArquivo('');
    setSelectedFile(null);
    setSelecionarTodos(false);
    setEquipamentosSelecionados([]);
    setListaEquipamentos(equipaments || []);
    setValue('dt_inicio', undefined);
    setValue('dt_encerramento', undefined);
    setValue('arquivo', undefined);
    setSelectedNumeroApolice([]);
    setValue('nu_apolice', undefined);
  };

  const handleDataInicio = (date: Date) => {
    clearErrors('dt_inicio');
    setValue('dt_inicio', date ? date.toISOString() : undefined);
    setDtInicio(date);

    let data_final = date;
    if (data_final) {
      data_final = getDayAfterXMonths(data_final, 12);
    }
    setValue(
      'dt_encerramento',
      data_final ? data_final.toISOString() : undefined,
    );
    setDtEncerramento(data_final);
  };

  const handleDataRenovacao = (date: Date) => {
    clearErrors('dt_encerramento');
    setValue('dt_encerramento', date ? date.toISOString() : undefined);
    setDtEncerramento(date);
  };

  const nomeArquivoValido = (nome: string) => {
    return nome.split('.').length === 2;
  };

  const handleFileChange = (_file: File | null) => {
    clearErrors('arquivo');

    if (_file && !nomeArquivoValido(_file.name)) {
      setError('arquivo', {
        type: 'invalidName',
        message: 'Nome arquivo inválido',
      });
      setSelectedFile(null);
      setNomeArquivo('');
      setValue('arquivo', undefined);
      return;
    }

    if (_file) {
      if (_file.type !== 'application/pdf') {
        setError('arquivo', {
          type: 'invalidType',
          message: 'Apenas arquivos PDF são aceitos',
        });
        setSelectedFile(null);
        setNomeArquivo('');
        setValue('arquivo', undefined);
        return;
      }

      if (_file.size > 5242880) {
        setError('arquivo', {
          type: 'maxSize',
          message: 'O arquivo deve ter no máximo 5MB',
        });
        setSelectedFile(null);
        setNomeArquivo('');
        setValue('arquivo', undefined);
        return;
      }
    }

    setSelectedFile(_file);
    setNomeArquivo(_file ? _file.name : '');
    setValue('arquivo', _file ? _file.name : undefined);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setNomeArquivo('');
    setValue('arquivo', undefined);
  };

  const onChangeSelecionarTodos = () => {
    setSelecionarTodos(!selecionarTodos);

    const lista = listaEquipamentos.map(eq => {
      return { ...eq, selecionado: !selecionarTodos };
    });
    setListaEquipamentos(lista);

    const equipamentos = lista.filter(eq => eq.selecionado);
    setEquipamentosSelecionados(equipamentos);
  };

  const onChangeSelecionar = (item: SummarizedEquipments) => {
    const lista = listaEquipamentos.map(eq => {
      if (eq.id_equipamento === item.id_equipamento) {
        return { ...eq, selecionado: !eq.selecionado };
      }
      return eq;
    });
    setListaEquipamentos(lista);

    const todosSelecionados = lista.every(eq => eq.selecionado);
    setSelecionarTodos(todosSelecionados);

    const equipamentos = lista.filter(eq => eq.selecionado);
    setEquipamentosSelecionados(equipamentos);
  };

  async function salvar(form: any) {
    const { _formState } = control;
    if (!_formState.isValid) return;

    setFetching(true);

    // eslint-disable-next-line no-shadow
    const { nu_apolice, dt_inicio, dt_encerramento, observacao } = form;

    const formData = new FormData();
    formData.append('nu_apolice', nu_apolice);
    formData.append('dt_inicio', dt_inicio.toISOString().substring(0, 10));
    formData.append(
      'dt_encerramento',
      dt_encerramento.toISOString().substring(0, 10),
    );
    formData.append('observacoes', observacao);
    formData.append('id_contrato', id_contrato);
    formData.append('arquivo', selectedFile as Blob);
    formData.append(
      'equipamentos',
      JSON.stringify(equipamentosSelecionados.map(eq => eq.id_equipamento)),
    );

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    let request;
    if (seguro) {
      request = api.put(`/apolices/${seguro.id}`, formData, config);
    } else {
      request = api.post(`/apolices`, formData, config);
    }
    request
      .then(() => {
        handleClose();
        toast({
          title: `Apólice ${
            seguro && seguro.id ? 'alterada com sucesso' : 'criada com sucesso'
          }`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('insurance-list');
        queryClient.invalidateQueries('all-insurance');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          toast({
            title: 'Erro',
            description: error.response.data.detail,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `Erro ao ${seguro ? 'editar' : 'salvar'} apólice`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const getIconButton = () => {
    return isEdit ? (
      <IconButton
        aria-label="Editar"
        onClick={handleOpen}
        icon={<Icon as={FiEdit} />}
      />
    ) : (
      <IconButton
        aria-label="Visualizar"
        onClick={handleOpen}
        icon={<Icon as={FiEye} />}
      />
    );
  };

  const onChangeSelectedInsurance = (changes: Option[]) => {
    const desabilitar = formattedInsuranceList.map(insurance => {
      return { ...insurance, disabled: !!changes.length };
    });

    if (changes.length) {
      const contemNaLista = formattedInsuranceList.find(item => {
        return item.value === changes[0]?.value;
      });
      if (!contemNaLista) {
        setFormattedInsuranceList([
          ...desabilitar,
          {
            value: changes[0]?.value,
            label: `${changes[0]?.label} (novo)`,
            disabled: true,
          },
        ]);
      } else {
        const obj = insuranceList?.filter(item => {
          return item.nu_apolice === changes[0]?.value;
        });
        if (obj && obj.length) {
          setValue('dt_inicio', obj[0]?.dt_inicio);
          setValue('dt_encerramento', obj[0]?.dt_encerramento);
          setValue('arquivo', obj[0]?.arquivo);
          setDtInicio(parseDate(obj[0]?.dt_inicio));
          setDtEncerramento(parseDate(obj[0]?.dt_encerramento));
          setNomeArquivo(obj[0]?.arquivo);
        }
        setFormattedInsuranceList(desabilitar);
      }
    } else {
      limparAutocomplete();
      setFormattedInsuranceList(desabilitar);
    }
  };

  const limparAutocomplete = () => {
    setValue('nu_apolice', undefined);
    setValue('dt_inicio', undefined);
    setValue('dt_encerramento', undefined);
    setValue('observacao', undefined);
    setValue('arquivo', undefined);
    setDtInicio(undefined);
    setDtEncerramento(undefined);
    setNomeArquivo(undefined);
  };

  const numeroApoliceExistente = insuranceList?.find(it => {
    return it.nu_apolice === selectedNumeroApolice?.[0]?.value;
  });

  return (
    <>
      {seguro ? (
        getIconButton()
      ) : (
        <HStack justifyContent="end" fontWeight="bold" alignItems="center">
          <Text
            cursor="pointer"
            onClick={handleOpen}
            display="flex"
            alignItems="center"
            gridGap={1}
          >
            <Icon as={BsPlusCircle} /> Adicionar apólice
          </Text>
        </HStack>
      )}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent>
          <Stack as="form" onSubmit={handleSubmit(salvar)}>
            <DrawerCloseButton />
            <DrawerHeader mb={4}>
              <Heading fontSize="xl">
                {seguro ? (isEdit ? 'Editar' : 'Visualizar') : 'Adicionar'}{' '}
                apólice
              </Heading>
            </DrawerHeader>

            <DrawerBody>
              <Stack gridGap={2}>
                <Stack>
                  {/* <Input */}
                  {/*  name="nu_apolice" */}
                  {/*  type="number" */}
                  {/*  label="N° da apólice" */}
                  {/*  register={register} */}
                  {/*  fontSize="14px" */}
                  {/*  isRequired */}
                  {/*  error={errors?.nu_apolice} */}
                  {/*  isDisabled={fetching || !!seguro} */}
                  {/* /> */}
                  <FormControl isInvalid={errors?.nu_apolice}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      display="flex"
                    >
                      N° da apólice{' '}
                      <span style={{ color: 'red' }}>&ensp;* </span>
                    </FormLabel>
                    <InputGroup
                      border={errors?.nu_apolice ? '1px solid red' : ''}
                      borderRadius="4px"
                      display="block"
                      zIndex={3}
                    >
                      <MultiSelect
                        value={selectedNumeroApolice || []}
                        options={formattedInsuranceList}
                        onChange={(changes: Option[]) => {
                          clearErrors();
                          setSelectedNumeroApolice(changes);
                          setValue(
                            'nu_apolice',
                            changes.length ? changes[0]?.value : undefined,
                          );
                          onChangeSelectedInsurance(changes);
                        }}
                        labelledBy="Selecione"
                        overrideStrings={{
                          ...MultiSelectStrings,
                          allItemsAreSelected: selectedNumeroApolice?.length
                            ? selectedNumeroApolice[0]?.label
                            : '',
                        }}
                        isLoading={isLoadingInsuranceList}
                        filterOptions={(options, filter) => {
                          return options.filter(
                            option =>
                              option?.label.toLowerCase() ===
                              filter.toLowerCase(),
                          );
                        }}
                        isCreatable={!selectedNumeroApolice?.length}
                        hasSelectAll={false}
                        disabled={!!seguro}
                      />
                    </InputGroup>
                    <FormErrorMessage>
                      {errors?.nu_apolice?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
                <HStack alignItems="top">
                  <Datepicker
                    label="Data de início"
                    required
                    onChange={e => handleDataInicio(e)}
                    name="dt_inicio"
                    isClearable={!fetching && !seguro}
                    selectedDate={dt_inicio}
                    error={errors?.dt_inicio}
                    isDisabled={
                      fetching || !!seguro || !!numeroApoliceExistente
                    }
                  />
                  <Datepicker
                    label="Data de encerramento"
                    required
                    onChange={e => handleDataRenovacao(e)}
                    name="dt_encerramento"
                    isClearable={!fetching && !seguro}
                    selectedDate={dt_encerramento}
                    error={errors?.dt_encerramento}
                    isDisabled={
                      fetching || !!seguro || !!numeroApoliceExistente
                    }
                  />
                </HStack>
                <Textarea
                  name="observacao"
                  label="Observações"
                  resize="none"
                  error={errors?.observacao}
                  fontSize="15px"
                  placeholder="Entre com a observação"
                  register={register}
                  isDisabled={fetching || isView}
                />
                <FileUploader
                  isDisabled={fetching || isView || !!numeroApoliceExistente}
                  fileName={nomeArquivo}
                  onChange={handleFileChange}
                  clearFile={clearFile}
                  label="Anexar apólice"
                  errors={errors.arquivo}
                />
                <Text marginTop={3} fontSize="14px" fontWeight="bold">
                  Equipamentos assegurados
                </Text>
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>
                        {listaEquipamentos.length > 0 && (
                          <Checkbox
                            colorScheme="b"
                            isChecked={selecionarTodos}
                            onChange={onChangeSelecionarTodos}
                            fontSize="sm"
                            isDisabled={fetching || isView}
                          />
                        )}
                      </Th>
                      <Th w={150}>Código SKU</Th>
                      <Th>Modelo</Th>
                      <Th>Qtde</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {listaEquipamentos.length ? (
                      listaEquipamentos.map(eq => (
                        <Tr key={eq.id_equipamento}>
                          <Td verticalAlign="top">
                            <Checkbox
                              colorScheme="b"
                              isChecked={eq.selecionado}
                              onChange={() => {
                                onChangeSelecionar(eq);
                              }}
                              fontSize="sm"
                              isDisabled={fetching || isView}
                            />
                          </Td>
                          <Td w={150} verticalAlign="top">
                            {eq.cd_equipamento}
                          </Td>
                          <Td verticalAlign="top">
                            {eq.nm_modeloequipamento || '-'}
                          </Td>
                          <Td verticalAlign="top">{eq.quantity}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={4} textAlign="center">
                          <Text>Nenhum equipamento encontrado</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Stack>
            </DrawerBody>
            <DrawerFooter gridGap={3}>
              <Button variant="secondary" onClick={() => handleClose()}>
                Fechar
              </Button>
              {!isView && (
                <Button type="submit" variant="primary" isDisabled={fetching}>
                  Salvar {fetching && <Spinner ml={2} />}
                </Button>
              )}
            </DrawerFooter>
          </Stack>
        </DrawerContent>
      </Drawer>
    </>
  );
};
